import type { BrandVersion, ClientInfo } from '@/types/auth.types';

const COLLECT_TIMEOUT_MS = 500;
// The API drops the whole object if a string or list is longer than these.
const MAX_TEXT_LENGTH = 512;
const MAX_LIST_LENGTH = 20;

// Browser APIs that TypeScript's DOM types don't include yet (Chromium only).
interface UserAgentData {
  brands?: BrandVersion[];
  mobile?: boolean;
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<{
    platformVersion?: string;
    architecture?: string;
    model?: string;
    fullVersionList?: BrandVersion[];
  }>;
}

type ExtendedNavigator = Navigator & {
  userAgentData?: UserAgentData;
  deviceMemory?: number;
  connection?: { effectiveType?: string; downlink?: number; rtt?: number; saveData?: boolean };
};

// One unavailable API must not lose the rest, so every read is guarded.
const read = <T>(getter: () => T): T | undefined => {
  try {
    return getter() ?? undefined;
  } catch {
    return undefined;
  }
};

const collectSync = (): ClientInfo => {
  const nav = navigator as ExtendedNavigator;
  const uaData = read(() => nav.userAgentData);

  return {
    userAgent: read(() => nav.userAgent),
    platform: read(() => nav.platform),
    vendor: read(() => nav.vendor),
    language: read(() => nav.language),
    languages: read(() => nav.languages.slice(0, MAX_LIST_LENGTH)),
    timezone: read(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
    cookieEnabled: read(() => nav.cookieEnabled),
    userAgentData: uaData
      ? {
          brands: read(() => uaData.brands?.map(({ brand, version }) => ({ brand, version }))),
          mobile: read(() => uaData.mobile),
          platform: read(() => uaData.platform),
        }
      : undefined,
    screen: read(() => ({
      width: window.screen.width,
      height: window.screen.height,
      availWidth: window.screen.availWidth,
      availHeight: window.screen.availHeight,
      colorDepth: window.screen.colorDepth,
      pixelDepth: window.screen.pixelDepth,
      orientation: read(() => window.screen.orientation.type),
    })),
    viewport: read(() => ({ width: window.innerWidth, height: window.innerHeight })),
    devicePixelRatio: read(() => window.devicePixelRatio),
    maxTouchPoints: read(() => nav.maxTouchPoints),
    hardwareConcurrency: read(() => nav.hardwareConcurrency),
    deviceMemory: read(() => nav.deviceMemory),
    connection: read(() =>
      nav.connection
        ? {
            effectiveType: nav.connection.effectiveType,
            downlink: nav.connection.downlink,
            rtt: nav.connection.rtt,
            saveData: nav.connection.saveData,
          }
        : undefined
    ),
  };
};

// Drops undefined fields and clips strings so the API accepts the payload.
const toPayload = <T extends object>(value: T): T =>
  JSON.parse(
    JSON.stringify(value, (_key, field: unknown) =>
      typeof field === 'string' ? field.slice(0, MAX_TEXT_LENGTH) : field
    )
  ) as T;

/**
 * Browser, device and screen details sent with the login request for the
 * log-in history (DGR-1835). Only what the browser exposes is included. The
 * Chromium-only high-entropy values are awaited for at most 500 ms.
 */
export const collectClientInfo = async (): Promise<ClientInfo> => {
  const info = collectSync();
  const uaData = read(() => (navigator as ExtendedNavigator).userAgentData);

  if (uaData?.getHighEntropyValues && info.userAgentData) {
    const highEntropy = await Promise.race([
      uaData
        .getHighEntropyValues(['platformVersion', 'architecture', 'model', 'fullVersionList'])
        .catch(() => undefined),
      new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), COLLECT_TIMEOUT_MS)),
    ]);
    if (highEntropy) {
      info.userAgentData = {
        ...info.userAgentData,
        platformVersion: highEntropy.platformVersion,
        architecture: highEntropy.architecture,
        model: highEntropy.model,
        fullVersionList: highEntropy.fullVersionList?.map(({ brand, version }) => ({ brand, version })),
      };
    }
  }

  return toPayload(info);
};
